import { some } from "lodash";
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissionValidator } from "../api/UserPermissionValidators";

type GuardedRouteProps = React.PropsWithChildren<{}>;

function isEditReportDetail(path: string): boolean {
  const expression = new RegExp("amendment|detail|post-rate|system|logs");
  return expression.test(path);
}
const GuardedRoute = ({ children }: GuardedRouteProps) => {
  const {
    canViewUsageReport,
    canManageBlueprints,
    canManageReports,
    canManageUsers,
    canManageWorkflows,
    canAddReport,
    canAddBlueprints,
    canAddNotes,
    canManageCompanies,
    companies,
    subcompanies,
    canComplete,
    canNavAmendment,
    canClientInProgress,
    canNavPendingReview,
    canManageConsent,
    canNavReviewed,
    canInternalQaEdit,
    canClientQaEdit,
    canManageAdminView,
    canEditAmendment360,
    legacy,
  } = usePermissionValidator();
  const location = useLocation();
  const inCheckTypes = location.pathname.includes("check-types");
  const inEditCompanies =
    location.pathname.includes("companies") &&
    location.pathname.includes("edit");
  const inCompanyVert =
    location.pathname.includes("companies") &&
    !location.pathname.includes("subcompany") &&
    (location.pathname.includes("settings") ||
      location.pathname.includes("subcompanies"));
  const inEditSubcompanies =
    location.pathname.includes("companies") &&
    location.pathname.includes("subcompany") &&
    location.pathname.includes("edit");
  const inCompanySubVert =
    location.pathname.includes("companies") &&
    location.pathname.includes("subcompany") &&
    location.pathname.includes("settings");
  const inWorkflows = location.pathname.includes("workflows");
  const inUsers = location.pathname.includes("users");
  const inReports = isEditReportDetail(location.pathname);
  const inAddReport = location.pathname.includes("person/new");
  const inAddBlueprint = location.pathname.includes("check-types/new");
  const inLogs = location.pathname.includes("logs");
  const inCompaniesBehaviorKitEdit =
    location.pathname.includes("companies") &&
    location.pathname.includes("behaviorkit") &&
    location.pathname.includes("edit");
  const inCompleted = location.pathname.includes("completed");
  const inAmendment = location.pathname.includes("amendment");
  const inClientInProgress = location.pathname.includes("in_progress");
  const inPendingReview = location.pathname.includes("pending_review");
  const inReviewed = location.pathname.includes("reviewed");
  const inConsent = location.pathname.includes("consent");
  const inSelfServe = location.pathname.includes("self-serve");
  const inInternalQa =
    location.pathname.includes("reports") && location.pathname.includes("iqa");
  const inClientQa =
    location.pathname.includes("reports") && location.pathname.includes("cqa");
  const in360MisconductQa =
    location.pathname.includes("360") &&
    location.pathname.includes("misconduct-qa");
  const inLegacyUsers = location.pathname.includes("legacy/users");
  const in360Summary =
    location.pathname.includes("/360/") &&
    location.pathname.includes("/summary");
  const in360Amendment =
    location.pathname.includes("/360") &&
    location.pathname.includes("/amendment");
  const inUsageReport = location.pathname.includes("usage-report");
  const inCustomBehaviorPrompts = location.pathname.includes(
    "custom-behavior-prompts"
  );
  if (
    some([
      inUsageReport && !canViewUsageReport,
      inCheckTypes && !canManageBlueprints,
      inWorkflows && !canManageWorkflows,
      inReports && !canManageReports,
      inUsers && !canManageUsers,
      inAddReport && !canAddReport,
      inEditCompanies && !canManageCompanies,
      inEditSubcompanies && !subcompanies.canViewDetails,
      inCompanyVert && !companies.canViewDetails,
      inCompanySubVert && !subcompanies.canViewDetails,
      inAddBlueprint && !canAddBlueprints,
      inLogs && !canAddNotes,
      inCompaniesBehaviorKitEdit && !canManageCompanies,
      inCompleted && !canComplete,
      inAmendment && !canNavAmendment,
      inClientInProgress && !canClientInProgress,
      inPendingReview && !canNavPendingReview,
      inReviewed && !canNavReviewed,
      inConsent && !canManageConsent,
      inSelfServe && !canManageAdminView,
      inInternalQa && !canInternalQaEdit,
      inClientQa && !canClientQaEdit,
      in360MisconductQa && !canInternalQaEdit,
      inLegacyUsers && !legacy?.canGetUsers,
      in360Summary && !canManageReports,
      in360Amendment && !canEditAmendment360,
      inCustomBehaviorPrompts && !canManageAdminView,
    ])
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export { GuardedRoute };
